import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { ProductCard } from './homeData';
import './Home.css';

const Home: React.FC = () => {
  const cms = useSelector((state: RootState) => state.cms);
  const recentViews = useSelector((state: RootState) => state.recentViews.items);
  const { 
    bannerSlides, 
    subBanners, 
    homeSecuritySection, 
    architectureSection, 
    electricalSection, 
    appliancesSection 
  } = cms.home;
  
  // Derive journeyItems from actual database products
  const { dbProducts } = require('../../data/db');
  const journeyItems = dbProducts.products.slice(12, 16).map((p: any) => ({
    name: p.name,
    image: p.image,
    link: `/product/${p.id}`
  }));
  
  const bestsellerProducts = dbProducts.products.slice(0, 10).map((p: any) => ({
    id: p.id,
    name: p.name,
    brand: p.brand,
    rating: parseFloat(p.rating),
    reviewsCount: p.reviewsCount,
    price: p.price,
    oldPrice: p.oldPrice,
    discount: p.discount,
    isDeal: p.isDeal,
    image: p.image
  }));

  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);

  const categoryKeys = [
    { id: 'security', name: 'Home Security', key: 'Home Security' },
    { id: 'power', name: 'Power Tools', key: 'Power Tools' },
    { id: 'lighting', name: 'Lighting & Light', key: 'Lighting' },
    { id: 'appliances', name: 'Home Appliances', key: 'Appliances' },
    { id: 'electricals', name: 'Electrical Services', key: 'Electricals' }
  ];

  const relatedCategories = categoryKeys.map((item) => {
    // Dynamic real-time product image from live database matching category key
    const firstProduct = dbProducts.products.find((p: any) => p.category === item.key);
    // Dynamic real-time sum of stockQuantity across all products in this category
    const dynamicStock = dbProducts.products
      .filter((p: any) => p.category === item.key)
      .reduce((sum: number, p: any) => sum + (p.stockQuantity || 0), 0);

    return {
      id: item.id,
      name: item.name,
      image: firstProduct ? firstProduct.image : '/assets/images/cp_indoor.png',
      count: dynamicStock,
      categoryKey: item.key
    };
  });

  const getFilteredProducts = () => {
    const selectedKey = categoryKeys[activeCategoryIndex]?.key || 'Home Security';
    return dbProducts.products.filter((p: any) => p.category === selectedKey).slice(0, 10);
  };

  const activeProducts = getFilteredProducts().map((p: any) => ({
    id: p.id,
    name: p.name,
    brand: p.brand,
    rating: parseFloat(p.rating || '4.0'),
    reviewsCount: p.reviewsCount || 10,
    price: p.price,
    oldPrice: p.oldPrice,
    discount: p.discount,
    isDeal: p.isDeal,
    image: p.image
  }));

  // Timed banner rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bannerSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [bannerSlides.length]);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + bannerSlides.length) % bannerSlides.length);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % bannerSlides.length);
  };

  // Scroll handler refs for horizontal sliders
  const recentScrollRef = useRef<HTMLDivElement>(null);
  const bestScrollRef = useRef<HTMLDivElement>(null);
  const securityScrollRef = useRef<HTMLDivElement>(null);
  const archScrollRef = useRef<HTMLDivElement>(null);
  const electScrollRef = useRef<HTMLDivElement>(null);
  const applianceScrollRef = useRef<HTMLDivElement>(null);
  const viewedScrollRef = useRef<HTMLDivElement>(null);

  const scrollLeft = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollBy({ left: -316, behavior: 'smooth' });
    }
  };

  const scrollRight = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollBy({ left: 316, behavior: 'smooth' });
    }
  };

  // Helper function to render a single premium product card
  const renderProductCard = (p: ProductCard) => {
    return (
      <Link to={`/product/${p.id}`} key={p.id} className="product-vertical-grid-card" style={{ textDecoration: 'none', color: 'inherit' }}>
        {p.isDeal && <span className="pwa-deal-badge">DEAL OF THE DAY</span>}
        <div className="product-card-upper">
          <div className="pwa-image">
            <img src={p.image} alt={p.name} />
          </div>
        </div>
        <div className="product-card-lower">
          <div className="product-card-lower-inner">
            <div className="name" title={p.name}>
              <span>{p.name}</span>
            </div>
            <div className="brand-pwa-name">
              <span>{p.brand}</span>
            </div>
            <div className="prod-price-row">
              <div className="prod-selling-price">
                <span>{p.price}</span>
              </div>
              {p.oldPrice && (
                <div className="price">
                  <span className="prod-mrp">{p.oldPrice}</span>
                  {p.discount && <span className="prod-discount">{p.discount}</span>}
                </div>
              )}
              <span className="taxes-inc">Taxes Included</span>
            </div>
          </div>
          <div className="right-btn-container">
            <button className="btn tertiary-btn" onClick={(e) => e.preventDefault()}>BUY NOW</button>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="home-page-redesign">
      
      {/* 2. Hero Section: 2/3 banner carousel + 1/3 stacked sub-banners block */}
      <div className="banner-full-width-wrapper">
        <div className="home-inner-centered-container">
          <div className="banner-hero-split-grid">
            
            {/* Left 2/3 Slider Carousel */}
            <div className="banner-slider-carousel-col">
              <div className="banner-carousel-window">
                <div 
                  className="banner-carousel-track" 
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {bannerSlides.map((slide, idx) => (
                    <div key={idx} className="banner-slide-item">
                      <div 
                        className="banner-slide-img" 
                        style={{ backgroundImage: `url(${slide.image})` }}
                      />
                    </div>
                  ))}
                </div>

                {/* Progress Bar Indicators */}
                <div className="carousel-progress-bars">
                  {bannerSlides.map((_, idx) => (
                    <div 
                      key={idx} 
                      className={`progress-item-bar ${idx === currentSlide ? 'active' : ''}`}
                      onClick={() => setCurrentSlide(idx)}
                    >
                      <div className="progress-bar-fill" />
                    </div>
                  ))}
                </div>

                {/* Nav Arrows */}
                <button className="banner-nav-arrow-btn prev-arrow-btn" onClick={handlePrevSlide}>
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <button className="banner-nav-arrow-btn next-arrow-btn" onClick={handleNextSlide}>
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Right 1/3 Stacked Sub Banners */}
            <div className="banner-side-stacked-banners">
              {subBanners.slice(0, 2).map((sb, idx) => (
                <div 
                  key={idx} 
                  className="side-banner-card-item"
                  style={{ backgroundImage: `url(${sb.image})` }}
                />
              ))}
            </div>

          </div>

          {/* Banner Tabs Directly Below Carousel */}
          <div className="home-banner-caption-tabs">
            {bannerSlides.map((slide, idx) => (
              <div 
                key={idx} 
                className={`banner-subCard-item ${idx === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(idx)}
              >
                <p className="bold-caption">{slide.title}</p>
                <p className="sub-caption">{slide.subtitle}</p>
              </div>
            ))}
          </div>

          {/* Unified Feature Strip Row (Directly below banner block) */}
          <div className="pwa-features-unified-strip">
            <div className="features-bar-inner">
              <div className="f-item">
                <div className="f-icon-box">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="3" width="15" height="13" />
                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                    <circle cx="5.5" cy="18.5" r="2.5" />
                    <circle cx="18.5" cy="18.5" r="2.5" />
                  </svg>
                </div>
                <div className="f-text-box">
                  <span className="title">Fastest Delivery</span>
                  <span className="desc">Delivery in 24/H</span>
                </div>
              </div>

              <div className="f-item">
                <div className="f-icon-box">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
                  </svg>
                </div>
                <div className="f-text-box">
                  <span className="title">24 Hours Return</span>
                  <span className="desc">100% money-back guarantee</span>
                </div>
              </div>

              <div className="f-item">
                <div className="f-icon-box">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </div>
                <div className="f-text-box">
                  <span className="title">Secure Payment</span>
                  <span className="desc">Your money is safe</span>
                </div>
              </div>

              <div className="f-item phone-call-f">
                <div className="f-icon-box">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M6.62 10.79a15.15 15.15 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.11-.27c1.12.45 2.33.69 3.58.69a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.24 2.46.69 3.58a1 1 0 0 1-.27 1.11l-2.2 2.2z" />
                  </svg>
                </div>
                <div className="f-text-box">
                  <span className="title">365 Days Help Desk</span>
                  <span className="desc"><b>+91-9667722651</b></span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* 3. Hi User, Continue Your Journey block (Linear Gradient matched perfectly to screenshot) */}
      <div className="journey-block-item journey-continue-journey">
        <div className="home-inner-centered-container">
          <h3 className="journey-section-title">Hi User, Continue Your Journey</h3>
          <div className="journey-grid-products">
            {journeyItems.slice(0, 4).map((item: any, idx: number) => (
              <Link to={item.link} key={idx} className="journey-square-product">
                <div className="journey-img-box">
                  <img src={item.image} alt={item.name} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* 4. Related To Items You Have Viewed block */}
      <div className="journey-block-item journey-related-viewed">
        <div className="home-inner-centered-container">
          <h3 className="journey-section-title">Related To Items You Have Viewed</h3>
          
          {/* Top Row: Category rectangular cards matching figma exactly */}
          <div className="viewed-grid-categories">
            {relatedCategories.map((item: any, idx: number) => {
              const isActive = activeCategoryIndex === idx;
              return (
                <div 
                  key={idx} 
                  className={`viewed-category-card ${isActive ? 'active' : ''}`}
                  onClick={() => setActiveCategoryIndex(idx)}
                >
                  <div className="viewed-category-img-box">
                    <img src={item.image} alt={item.name} />
                  </div>
                  <div className="viewed-category-divider" />
                  <div className="viewed-category-stat-row">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                      <line x1="12" y1="22.08" x2="12" y2="12" />
                    </svg>
                    <span>{item.count}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom Row: Horizontal product slider matching figma exactly */}
          <div className="slider-relative-container">
            <button className="slider-nav-circle-btn prev-btn" onClick={() => scrollLeft(viewedScrollRef)}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="products-scroll-section-layout hidden-scrollbar" ref={viewedScrollRef}>
              {activeProducts.map((p: any) => renderProductCard(p))}
            </div>
            <button className="slider-nav-circle-btn next-btn" onClick={() => scrollRight(viewedScrollRef)}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

        </div>
      </div>

      {/* 5. Recent View Section (F7 background, standalone centered container) */}
      {recentViews.length > 0 && (
        <div className="category-section-block bg-grey-section">
          <div className="home-inner-centered-container">
            <div className="view-all-strip-header">
              <h2>Recent View</h2>
            </div>
            
            <div className="slider-relative-container">
              <button className="slider-nav-circle-btn prev-btn" onClick={() => scrollLeft(recentScrollRef)}>
                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <div className="products-scroll-section-layout hidden-scrollbar" ref={recentScrollRef}>
                {recentViews.map((p: any) => renderProductCard(p))}
              </div>
              <button className="slider-nav-circle-btn next-btn" onClick={() => scrollRight(recentScrollRef)}>
                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 6. Best Seller Section (White background) */}
      <div className="category-section-block">
        <div className="home-inner-centered-container">
          <div className="view-all-strip-header">
            <h2>Best Seller</h2>
          </div>
          
          <div className="slider-relative-container">
            <button className="slider-nav-circle-btn prev-btn" onClick={() => scrollLeft(bestScrollRef)}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="products-scroll-section-layout hidden-scrollbar" ref={bestScrollRef}>
              {bestsellerProducts.map((p: any) => renderProductCard(p))}
            </div>
            <button className="slider-nav-circle-btn next-btn" onClick={() => scrollRight(bestScrollRef)}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* 7. Home Security Section (F7 background, horizontal brand log bar + product slider + 5 subcategory blue tiles) */}
      <div className="category-section-block bg-grey-section">
        <div className="home-inner-centered-container">
          
          {/* Section Header */}
          <div className="view-all-strip-header">
            <h2>{homeSecuritySection.title}</h2>
            <span className="view-all-blue-link">View All</span>
          </div>

          {/* 1. Brand Row */}
          <div className="brand-strip-stacked-row">
            <div className="brand-head-label">Top Brand &amp; Related Category</div>
            <div className="brand-logos-grid-container">
              {homeSecuritySection.brands.map((b, idx) => (
                <div key={idx} className="brand-grid-logo-item">
                  <img src={b.logo} alt={b.name} />
                </div>
              ))}
            </div>
          </div>

          {/* 2. Product Slider */}
          <div className="slider-relative-container">
            <button className="slider-nav-circle-btn prev-btn" onClick={() => scrollLeft(securityScrollRef)}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="products-scroll-section-layout hidden-scrollbar" ref={securityScrollRef}>
              {homeSecuritySection.products.map((p) => renderProductCard(p))}
            </div>
            <button className="slider-nav-circle-btn next-btn" onClick={() => scrollRight(securityScrollRef)}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          {/* 3. Subcategories Blue Tiles (4 columns matching figma exactly) */}
          <div className="sub-category-blue-tiles-grid">
            {homeSecuritySection.subCategories.slice(0, 4).map((sc, idx) => (
              <div key={idx} className="subcategory-column-wrapper">
                <div className="subcategory-white-card">
                  <div className="tile-img-wrapper">
                    <img src={sc.image} alt={sc.name} />
                  </div>
                  <div className="tile-card-divider"></div>
                  <p className="tile-title-name">{sc.name}</p>
                </div>
                <button className="tile-solid-blue-btn">
                  <span>EXPLORE NOW</span>
                  <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="4" y1="12" x2="20" y2="12" />
                    <polyline points="13 5 20 12 13 19" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* 8. Architecture Lighting Section */}
      <div className="category-section-block">
        <div className="home-inner-centered-container">
          
          {/* Section Header */}
          <div className="view-all-strip-header">
            <h2>{architectureSection.title}</h2>
            <span className="view-all-blue-link">View All</span>
          </div>

          {/* 1. Brand Row */}
          <div className="brand-strip-stacked-row">
            <div className="brand-head-label">Top Brand &amp; Related Category</div>
            <div className="brand-logos-grid-container">
              {homeSecuritySection.brands.map((b, idx) => (
                <div key={idx} className="brand-grid-logo-item">
                  <img src={b.logo} alt={b.name} />
                </div>
              ))}
            </div>
          </div>

          {/* 2. Product Slider */}
          <div className="slider-relative-container">
            <button className="slider-nav-circle-btn prev-btn" onClick={() => scrollLeft(archScrollRef)}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="products-scroll-section-layout hidden-scrollbar" ref={archScrollRef}>
              {architectureSection.products.map((p) => renderProductCard(p))}
            </div>
            <button className="slider-nav-circle-btn next-btn" onClick={() => scrollRight(archScrollRef)}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          {/* 3. Subcategories Blue Tiles (4 columns matching figma exactly) */}
          <div className="sub-category-blue-tiles-grid">
            {architectureSection.subCategories.slice(0, 4).map((sc, idx) => (
              <div key={idx} className="subcategory-column-wrapper">
                <div className="subcategory-white-card">
                  <div className="tile-img-wrapper">
                    <img src={sc.image} alt={sc.name} />
                  </div>
                  <div className="tile-card-divider"></div>
                  <p className="tile-title-name">{sc.name}</p>
                </div>
                <button className="tile-solid-blue-btn">
                  <span>EXPLORE NOW</span>
                  <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="4" y1="12" x2="20" y2="12" />
                    <polyline points="13 5 20 12 13 19" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* 9. ELECTRICALS Section */}
      <div className="category-section-block bg-grey-section">
        <div className="home-inner-centered-container">
          
          {/* Section Header */}
          <div className="view-all-strip-header">
            <h2>{electricalSection.title}</h2>
            <span className="view-all-blue-link">View All</span>
          </div>

          {/* 1. Brand Row */}
          <div className="brand-strip-stacked-row">
            <div className="brand-head-label">Top Brand &amp; Related Category</div>
            <div className="brand-logos-grid-container">
              {homeSecuritySection.brands.map((b, idx) => (
                <div key={idx} className="brand-grid-logo-item">
                  <img src={b.logo} alt={b.name} />
                </div>
              ))}
            </div>
          </div>

          {/* 2. Product Slider */}
          <div className="slider-relative-container">
            <button className="slider-nav-circle-btn prev-btn" onClick={() => scrollLeft(electScrollRef)}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="products-scroll-section-layout hidden-scrollbar" ref={electScrollRef}>
              {electricalSection.products.map((p) => renderProductCard(p))}
            </div>
            <button className="slider-nav-circle-btn next-btn" onClick={() => scrollRight(electScrollRef)}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          {/* 3. Subcategories Blue Tiles (4 columns matching figma exactly) */}
          <div className="sub-category-blue-tiles-grid">
            {electricalSection.subCategories.slice(0, 4).map((sc, idx) => (
              <div key={idx} className="subcategory-column-wrapper">
                <div className="subcategory-white-card">
                  <div className="tile-img-wrapper">
                    <img src={sc.image} alt={sc.name} />
                  </div>
                  <div className="tile-card-divider"></div>
                  <p className="tile-title-name">{sc.name}</p>
                </div>
                <button className="tile-solid-blue-btn">
                  <span>EXPLORE NOW</span>
                  <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="4" y1="12" x2="20" y2="12" />
                    <polyline points="13 5 20 12 13 19" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* 10. APPLIANCES AND UTILITIES Section */}
      <div className="category-section-block">
        <div className="home-inner-centered-container">
          
          {/* Section Header */}
          <div className="view-all-strip-header">
            <h2>{appliancesSection.title}</h2>
            <span className="view-all-blue-link">View All</span>
          </div>

          {/* 1. Brand Row */}
          <div className="brand-strip-stacked-row">
            <div className="brand-head-label">Top Brand &amp; Related Category</div>
            <div className="brand-logos-grid-container">
              {homeSecuritySection.brands.map((b, idx) => (
                <div key={idx} className="brand-grid-logo-item">
                  <img src={b.logo} alt={b.name} />
                </div>
              ))}
            </div>
          </div>

          {/* 2. Product Slider */}
          <div className="slider-relative-container">
            <button className="slider-nav-circle-btn prev-btn" onClick={() => scrollLeft(applianceScrollRef)}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="products-scroll-section-layout hidden-scrollbar" ref={applianceScrollRef}>
              {appliancesSection.products.map((p) => renderProductCard(p))}
            </div>
            <button className="slider-nav-circle-btn next-btn" onClick={() => scrollRight(applianceScrollRef)}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          {/* 3. Subcategories Blue Tiles (4 columns matching figma exactly) */}
          <div className="sub-category-blue-tiles-grid">
            {appliancesSection.subCategories.slice(0, 4).map((sc, idx) => (
              <div key={idx} className="subcategory-column-wrapper">
                <div className="subcategory-white-card">
                  <div className="tile-img-wrapper">
                    <img src={sc.image} alt={sc.name} />
                  </div>
                  <div className="tile-card-divider"></div>
                  <p className="tile-title-name">{sc.name}</p>
                </div>
                <button className="tile-solid-blue-btn">
                  <span>EXPLORE NOW</span>
                  <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="4" y1="12" x2="20" y2="12" />
                    <polyline points="13 5 20 12 13 19" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* 11. Rich ABOUT US Block (F7 background) */}
      <div className="about-us-info-block-home bg-grey-section">
        <div className="home-inner-centered-container">
          <h2>ABOUT US</h2>
          <p className="lead-text">
            <b>Industrybull</b> is India’s leading B2B and B2C e-commerce platform, specializing in industrial products and smart technology. We bridge the gap between high-quality manufacturing and digital procurement, providing seamless supply chain solutions for businesses and individual consumers alike.
          </p>
          
          <div className="about-us-grid-details">
            <div className="about-col">
              <h4>Our Core Categories</h4>
              <p>We offer a comprehensive range of smart solutions designed for modern homes and offices:</p>
              <ul>
                <li><b>Security:</b> Video door phones, biometric attendance systems, and smart door locks.</li>
                <li><b>Connectivity:</b> IT networking solutions and high-speed Wi-Fi access points.</li>
                <li><b>Automation:</b> Full home automation systems and architectural lighting.</li>
              </ul>
            </div>

            <div className="about-col">
              <h4>Why Choose Industrybull?</h4>
              <p>We solve the common frustrations of online shopping by prioritizing speed and reliability.</p>
              <ul>
                <li><b>Fast &amp; Reliable Delivery:</b> While others face delays, our optimized supply chain ensures your orders arrive on time.</li>
                <li><b>Best Market Prices:</b> We offer competitive pricing and exclusive deals on bulk orders tailored to your specific inquiries.</li>
                <li><b>Seamless Experience:</b> Our platform is built to provide a smooth, flawless journey for both buyers and sellers.</li>
                <li><b>Customer-Centric:</b> We are committed to effortless delivery and total customer satisfaction.</li>
              </ul>
            </div>
          </div>

          <div className="about-mission-block">
            <h4>Our Mission:</h4>
            <p>To revolutionize industrial procurement by making it faster, more affordable, and completely hassle-free.</p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Home;
